"use client";

import { useRemoveFromCart, useSetQuantity } from "@trydig/centra/client";
import { Accordion } from "@/components/ui/Accordion";
import { CartListingCard } from "@/components/ui/CartListingCard";
import { useTranslations } from "@/i18n/client";
import { productCanonicalPath } from "@/i18n/routing";
import { type CartItem, useCart } from "./hooks";

export interface CartItemsProps {
	variant?: "default" | "checkout";
}

export const CartItems = ({ variant = "default" }: CartItemsProps) => {
	const { data } = useCart();

	if (!data?.items?.length) {
		return null;
	}

	return (
		<div className="flex flex-col gap-grid-component-spacing-2xl">
			{data.items.map((item) => (
				<CartItemRow key={item.line} item={item} variant={variant} />
			))}
		</div>
	);
};

export const CheckoutProductsAccordion = () => {
	const { data } = useCart();
	const t = useTranslations();

	if (!data?.items?.length) {
		return null;
	}

	return (
		<Accordion
			size="large"
			items={[
				{
					title: `${t("Products")} (${data.count})`,
					content: (
						<div className="pt-2">
							<CartItems variant="checkout" />
						</div>
					),
				},
			]}
		/>
	);
};

const CartItemRow = ({ item, variant }: { item: CartItem } & CartItemsProps) => {
	const setQuantity = useSetQuantity({ optimistic: true });
	const removeFromCart = useRemoveFromCart();

	const quantity = item.quantity ?? 1;
	const media = item.media?.[0];
	const product = item.product;

	const attributes: { label: string; value: string }[] = [];
	if (product?.variantName) {
		attributes.push({ label: "Color", value: product.variantName });
	}
	if (item.size) {
		attributes.push({ label: "Size", value: String(item.size) });
	}

	const hasDiscount = (item.priceEachAsNumber ?? 0) < (item.priceEachBeforeDiscountAsNumber ?? 0);

	return (
		<CartListingCard
			type={variant === "checkout" ? "checkout" : "shopping-cart"}
			image={{ src: media ?? "", alt: product?.name ?? "" }}
			name={product?.name ?? ""}
			// Don't link away from checkout — same reasoning as `showDeleteButton`
			// below, a stray tap shouldn't be able to abandon the flow.
			href={variant !== "checkout" && product?.uri ? productCanonicalPath(product.uri) : undefined}
			brand={product?.brandName ?? undefined}
			showBrand={false}
			attributes={attributes}
			price={item.priceEach}
			originalPrice={hasDiscount ? item.priceEachBeforeDiscount : undefined}
			quantity={quantity}
			onQuantityDecrement={() => setQuantity.mutate({ line: item.line!, quantity: quantity - 1 })}
			onQuantityIncrement={() => setQuantity.mutate({ line: item.line!, quantity: quantity + 1 })}
			showDeleteButton={variant !== "checkout"}
			onRemove={() => removeFromCart.mutate({ line: item.line! })}
		/>
	);
};
